// src/pages/admin/coupons/AdminCouponList.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { listCoupons } from '../../../api/adminCoupons';
import axios from '../../../api/axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminCouponUsersDialog from './AdminCouponUsersDialog';
import AdminCouponPosterDialog from './AdminCouponPosterDialog';

// 日本語型: API 行（サマリー + 基本情報のマージ結果）
type CouponRow = {
  id: number;
  name: string;
  code: string;                       // 表示は API 側で大文字
  link_code?: string | null;
  is_active: boolean;
  discount_type?: '' | 'percent' | 'amount' | 'fixed_price' | null;
  discount_value?: number | null;
  commission_type?: '' | 'percent' | 'amount' | null;
  commission_value?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  note?: string | null;

  // 分析サマリー
  bound_users_total: number;          // このクーポンを保有しているユーザー数
  clicks_total: number;               // 短縮リンクなどのクリック合計
};

const PAGE_SIZES = [10, 20, 50];

// 公開 URL のオリジン（必要なら index.html で window.__PUBLIC_ORIGIN__ を定義）
const PUBLIC_ORIGIN: string =
  (window as any).__PUBLIC_ORIGIN__ ||
  window.location.origin;

// link_code から実 URL を生成
const makeLinkUrl = (linkCode: string) => `${PUBLIC_ORIGIN}/c/${linkCode}`;

// ソートキーを拡張（bound_users_total / clicks_total を追加）
type SortKey = 'id' | 'name' | 'starts_at' | 'ends_at' | 'code' | 'bound_users_total' | 'clicks_total';
type SortOrder = 'asc' | 'desc';

const AdminCouponList: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // —— 認可簡易ガード（未ログイン直リンク対策）——
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token) navigate('/login/admin');
    else if (role !== 'admin') {
      alert(t('admin.common_access_denied') || 'アクセス権限がありません');
      navigate('/');
    }
  }, [navigate, t]);

  // —— 条件（サーバー側フィルタ & クライアント側フィルタ）——
  const [q, setQ] = useState('');                           // あいまい検索（サーバー送信・任意）
  const [activeFilter, setActiveFilter] =
    useState<'all' | 'active' | 'inactive'>('all');         // サーバー送信・任意
  const [startFilter, setStartFilter] = useState('');       // 期間フィルタ（クライアント側）
  const [endFilter, setEndFilter] = useState('');           // 期間フィルタ（クライアント側）
  const [typeFilter, setTypeFilter] =
    useState<'all' | 'discount' | 'rebate'>('all');         // タイプタブ（クライアント側）
  const [includeCompany, setIncludeCompany] = useState(false); // 法人クーポン表示

  // —— ページング ——
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);                      // 1 始まり

  // —— ソート ——
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc'); // 既定: ID 降順

  // —— データ＆状態 ——
  const [rows, setRows] = useState<CouponRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  // —— 明細ダイアログ状態 ——
  const [usersOpen, setUsersOpen] = useState(false);
  const [usersCouponId, setUsersCouponId] = useState<number | null>(null);
  const [usersCouponName, setUsersCouponName] = useState<string | null>(null);
  const [usersCouponNote, setUsersCouponNote] = useState<string | null>(null);

  // —— ポスター生成ダイアログ ——
  const [posterOpen, setPosterOpen] = useState(false);
  const [posterCoupon, setPosterCoupon] = useState<CouponRow | null>(null);

  // offset の計算
  const offset = useMemo(() => (page - 1) * pageSize, [page, pageSize]);

  // 日時フォーマッタ（言語切替に追随）
  const dtf = useMemo(
    () =>
      new Intl.DateTimeFormat(
        i18n.language === 'zh' ? 'zh-CN' : 'ja-JP',
        { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Tokyo' }
      ),
    [i18n.language]
  );

  // サーバーから取得（基本一覧 + summary を前端でマージ）
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setErrorText(null);

        const params: any = { limit: pageSize, offset };
        if (q.trim()) params.q = q.trim();
        if (activeFilter !== 'all') params.active = activeFilter === 'active';
        if (includeCompany) params.include_company = true;

        // 1) 基本一覧（/admin/coupons）
        // 2) サマリー（/admin/coupons/summary）
        const [baseRes, summaryRes] = await Promise.all([
          listCoupons(params),
          axios.get('/admin/coupons/summary'),
        ]);

        if (cancelled) return;

        // サマリーを map 化
        const totalsMap = new Map<number, { bound_users_total: number; clicks_total: number }>();
        const summaryArr = Array.isArray(summaryRes.data) ? summaryRes.data : [];
        summaryArr.forEach((s: any) => {
          totalsMap.set(Number(s.id), {
            bound_users_total: Number(s.bound_users_total ?? 0),
            clicks_total: Number(s.clicks_total ?? 0),
          });
        });

        // マージ
        const mapped: CouponRow[] = (baseRes || []).map((x: any) => {
          const totals = totalsMap.get(Number(x.id)) || { bound_users_total: 0, clicks_total: 0 };
          return {
            id: x.id,
            name: x.name,
            code: x.code,                 // API は大文字返却
            link_code: x.link_code,
            is_active: !!x.is_active,
            discount_type: x.discount_type ?? null,
            discount_value: x.discount_value ?? null,
            commission_type: x.commission_type ?? null,
            commission_value: x.commission_value ?? null,
            starts_at: x.starts_at ?? null,
            ends_at: x.ends_at ?? null,
            note: x.note ?? null,
            bound_users_total: totals.bound_users_total,
            clicks_total: totals.clicks_total,
          };
        });

        setRows(mapped);
      } catch (e: any) {
        setErrorText(
          e?.response?.data?.detail ||
          e?.message ||
          (t('admin.coupons.errors.fetch_failed') as string)
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [q, activeFilter, pageSize, offset, includeCompany, t]);

  // タブごとの件数（サーバー取得済み rows ベース）
  const typeCounts = useMemo(() => ({
    all:      rows.length,
    discount: rows.filter((r) => !!r.discount_type).length,
    rebate:   rows.filter((r) => !!r.commission_type).length,
  }), [rows]);

  // 期間フィルタ＋タイプフィルタ（クライアント側）
  const filteredRows = useMemo(() => {
    let result = rows;

    // タイプフィルタ
    if (typeFilter === 'discount') {
      result = result.filter((r) => !!r.discount_type);
    } else if (typeFilter === 'rebate') {
      result = result.filter((r) => !!r.commission_type);
    }

    // 期間フィルタ（クライアント側で重なり判定）
    if (startFilter || endFilter) {
      const fStart = startFilter ? new Date(startFilter) : null;
      const fEnd = endFilter ? new Date(endFilter) : null;
      result = result.filter((r) => {
        const rStart = r.starts_at ? new Date(r.starts_at) : null;
        const rEnd = r.ends_at ? new Date(r.ends_at) : null;
        const start = rStart ? rStart.getTime() : Number.NEGATIVE_INFINITY;
        const end = rEnd ? rEnd.getTime() : Number.POSITIVE_INFINITY;
        const fs = fStart ? fStart.getTime() : Number.NEGATIVE_INFINITY;
        const fe = fEnd ? fEnd.getTime() : Number.POSITIVE_INFINITY;
        return end >= fs && start <= fe;
      });
    }

    return result;
  }, [rows, typeFilter, startFilter, endFilter]);

  // ソート（クライアント側）
  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const dir = sortOrder === 'asc' ? 1 : -1;
      const getVal = (r: CouponRow) => {
        switch (sortKey) {
          case 'id': return r.id;
          case 'name': return r.name?.toLowerCase() || '';
          case 'code': return r.code?.toLowerCase() || '';
          case 'starts_at': return r.starts_at ? new Date(r.starts_at).getTime() : Number.NEGATIVE_INFINITY;
          case 'ends_at': return r.ends_at ? new Date(r.ends_at).getTime() : Number.POSITIVE_INFINITY;
          case 'bound_users_total': return r.bound_users_total;
          case 'clicks_total': return r.clicks_total;
          default: return r.id;
        }
      };
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [filteredRows, sortKey, sortOrder]);

  // OFF/還元 文言
  const formatBenefit = (r: CouponRow) => {
    const parts: string[] = [];
    if (r.discount_type && r.discount_value != null) {
      if (r.discount_type === 'percent') parts.push(`${r.discount_value}% ${t('admin.coupons.labels.off')}`);
      if (r.discount_type === 'amount') parts.push(`${t('admin.coupons.labels.jpy', { v: r.discount_value })} ${t('admin.coupons.labels.off')}`);
      if (r.discount_type === 'fixed_price') parts.push(`${t('admin.coupons.labels.fixed_price')} ${t('admin.coupons.labels.jpy', { v: r.discount_value })}`);
    }
    if (r.commission_type && r.commission_value != null) {
      if (r.commission_type === 'percent') parts.push(`${r.commission_value}% ${t('admin.coupons.labels.kangen')}`);
      if (r.commission_type === 'amount') parts.push(`${t('admin.coupons.labels.kangen')} ${t('admin.coupons.labels.jpy', { v: r.commission_value })}`);
    }
    return parts.length ? parts.join(' / ') : (t('admin.coupons.labels.none') as string);
  };

  // 期間の整形
  const formatPeriod = (r: CouponRow) => {
    const s = r.starts_at ? dtf.format(new Date(r.starts_at)) : '';
    const e = r.ends_at ? dtf.format(new Date(r.ends_at)) : '';
    if (!s && !e) return t('admin.coupons.labels.no_period') as string;
    if (!s && e) return `~ ${e}`;
    if (s && !e) return `${s} ~`;
    return `${s} ~ ${e}`;
  };

  // クリップボードにコピー
  const copyText = async (txt: string) => {
    try {
      await navigator.clipboard.writeText(txt);
      alert(t('admin.coupons.toasts.copied'));
    } catch {
      alert(t('admin.coupons.errors.copy_failed'));
    }
  };

  // 検索実行（Enter 対応）
  const onSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      setPage(1);
    }
  };

  return (
    <div className="p-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t('admin.coupons.list.title')}</h2>
        <button
          onClick={() => navigate('/admin/coupons/new')}
          className="px-4 py-2 rounded bg-coral text-white hover:opacity-90"
        >
          {t('admin.coupons.actions.create')}
        </button>
      </div>

      {/* タイプタブ */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {([
          { key: 'all',      label: t('admin.coupons.tabs.all'),      color: 'text-gray-600' },
          { key: 'discount', label: t('admin.coupons.tabs.discount'),  color: 'text-blue-600' },
          { key: 'rebate',   label: t('admin.coupons.tabs.rebate'),    color: 'text-emerald-600' },
        ] as const).map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => { setTypeFilter(key); setPage(1); }}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              typeFilter === key
                ? `border-coral ${color}`
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {label}
            <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${
              typeFilter === key ? 'bg-coral/10 text-coral' : 'bg-gray-100 text-gray-400'
            }`}>
              {typeCounts[key]}
            </span>
          </button>
        ))}
      </div>

      {/* フィルタバー */}
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {/* あいまい検索 */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('admin.coupons.filters.q')}</label>
            <input
              className="w-full border rounded px-3 py-2"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder={t('admin.coupons.placeholders.q') || ''}
            />
          </div>

          {/* 状態 */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('admin.coupons.filters.active')}</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={activeFilter}
              onChange={(e) => { setActiveFilter(e.target.value as any); setPage(1); }}
            >
              <option value="all">{t('admin.coupons.options.all')}</option>
              <option value="active">{t('admin.coupons.options.active')}</option>
              <option value="inactive">{t('admin.coupons.options.inactive')}</option>
            </select>
          </div>

          {/* 期間フィルタ（開始/終了）※ クライアント側でフィルタ */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('admin.coupons.columns.period')}（開始）</label>
            <input
              type="datetime-local"
              className="w-full border rounded px-3 py-2"
              value={startFilter}
              onChange={(e) => { setStartFilter(e.target.value); setPage(1); }}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('admin.coupons.columns.period')}（終了）</label>
            <input
              type="datetime-local"
              className="w-full border rounded px-3 py-2"
              value={endFilter}
              onChange={(e) => { setEndFilter(e.target.value); setPage(1); }}
            />
          </div>

          {/* ページサイズ */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('admin.common.page_size')}</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {/* ソートバー */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">ソート対象</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
            >
              <option value="id">ID</option>
              <option value="name">{t('admin.coupons.fields.name')}</option>
              <option value="code">{t('admin.coupons.fields.code')}</option>
              <option value="starts_at">{t('admin.coupons.columns.period')}（開始）</option>
              <option value="ends_at">{t('admin.coupons.columns.period')}（終了）</option>
              {/* 分析指標でソート */}
              <option value="bound_users_total">{t('admin.coupons.columns.bound_users') || 'バインド人数'}</option>
              <option value="clicks_total">{t('admin.coupons.columns.clicks') || 'クリック数'}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">順序</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as SortOrder)}
            >
              <option value="asc">昇順</option>
              <option value="desc">降順</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              className="px-3 py-2 border rounded hover:bg-gray-50"
              onClick={() => setPage(1)}
            >
              {t('admin.common.search') || '検索'}
            </button>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer whitespace-nowrap pb-0.5">
              <input
                type="checkbox"
                checked={includeCompany}
                onChange={e => { setIncludeCompany(e.target.checked); setPage(1); }}
                className="rounded"
              />
              法人クーポンを含む
            </label>
          </div>
        </div>
      </div>

      {/* ローディング/エラー/空表示/テーブル */}
      {loading ? (
        <div className="text-sm text-gray-500">{t('admin.common.loading')}</div>
      ) : errorText ? (
        <div className="text-sm text-red-600">{errorText}</div>
      ) : sortedRows.length === 0 ? (
        <div className="text-sm text-gray-500">{t('admin.coupons.list.empty')}</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
          <div className="md:hidden divide-y">
            {sortedRows.map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold break-words">{r.name}</div>
                    {r.note && <div className="text-xs text-gray-500 mt-0.5">（{r.note}）</div>}
                    <div className="text-xs text-gray-400 mt-0.5">ID: {r.id}</div>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}
                  >
                    {r.is_active ? t('admin.coupons.status.active') : t('admin.coupons.status.inactive')}
                  </span>
                </div>

                {/* コード + コピー */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm break-all">{r.code}</span>
                  <button
                    type="button"
                    onClick={() => copyText(r.code)}
                    className="shrink-0 text-xs px-2 py-1 border rounded hover:bg-gray-50"
                  >
                    {t('admin.coupons.actions.copy')}
                  </button>
                </div>

                {/* リンク URL + コピー */}
                {r.link_code ? (
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <a
                      href={makeLinkUrl(r.link_code)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-coral hover:underline break-all min-w-0"
                    >
                      {makeLinkUrl(r.link_code)}
                    </a>
                    <button
                      type="button"
                      onClick={() => copyText(makeLinkUrl(r.link_code!))}
                      className="shrink-0 text-xs px-2 py-0.5 border rounded hover:bg-gray-50"
                    >
                      {t('admin.coupons.actions.copy')}
                    </button>
                  </div>
                ) : null}

                {/* 特典 / 期間 / 集計 */}
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div>
                    <span className="text-gray-400">{t('admin.coupons.columns.benefit')}：</span>
                    <span className="text-gray-700">{formatBenefit(r)}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">{t('admin.coupons.columns.period')}：</span>
                    <span className="text-gray-700">{formatPeriod(r)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">{t('admin.coupons.columns.bound_users') || 'バインド人数'}：</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">
                      {r.bound_users_total.toLocaleString()}
                    </span>
                    {r.bound_users_total > 0 && (
                      <button
                        type="button"
                        onClick={() => { setUsersCouponId(r.id); setUsersCouponName(r.name); setUsersCouponNote(r.note || null); setUsersOpen(true); }}
                        className="text-[11px] px-1.5 py-0.5 border rounded hover:bg-gray-50 text-gray-600"
                      >
                        {t('admin.common.view') || '詳細'}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400">{t('admin.coupons.columns.clicks') || 'クリック数'}：</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-50 text-yellow-700">
                      {r.clicks_total.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* 操作 */}
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/coupons/${r.id}`)}
                    className="text-xs px-3 py-1.5 border rounded hover:bg-gray-50"
                  >
                    {t('admin.common.view') || '詳細'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPosterCoupon(r); setPosterOpen(true); }}
                    className="text-xs px-3 py-1.5 border rounded hover:bg-rose-50 text-rose-700 border-rose-300"
                  >
                    ポスター
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ▼ デスクトップ: テーブル表示 */}
          <table className="hidden md:table min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left w-20">ID</th>
                <th className="px-4 py-2 text-left">{t('admin.coupons.fields.name')}</th>
                <th className="px-4 py-2 text-left">{t('admin.coupons.fields.code')}</th>
                <th className="px-4 py-2 text-left hidden md:table-cell">{t('admin.coupons.columns.benefit')}</th>
                <th className="px-4 py-2 text-left hidden lg:table-cell">{t('admin.coupons.columns.period')}</th>
                {/* 分析サマリー列 */}
                <th className="px-4 py-2 text-left">{t('admin.coupons.columns.bound_users') || 'バインド人数'}</th>
                <th className="px-4 py-2 text-left">{t('admin.coupons.columns.clicks') || 'クリック数'}</th>
                <th className="px-4 py-2 text-left">{t('admin.coupons.fields.status')}</th>
                <th className="px-4 py-2 text-left">{t('admin.coupons.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-4 py-2">{r.id}</td>
                  <td className="px-4 py-2 font-medium">
                    <div>
                      <div>{r.name}</div>
                      {r.note && (
                        <div className="text-xs text-gray-500 mt-0.5">（{r.note}）</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    {/* コードとコピー */}
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{r.code}</span>
                      <button
                        type="button"
                        onClick={() => copyText(r.code)}
                        className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                        title={t('admin.coupons.actions.copy_code') || ''}
                      >
                        {t('admin.coupons.actions.copy')}
                      </button>
                    </div>

                    {/* リンクコード（ラベルなしで直接表示）＋ URL 表示＋コピー */}
                    {r.link_code ? (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-2 text-xs">
                          <a
                            href={makeLinkUrl(r.link_code)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-coral hover:underline break-all"
                          >
                            {makeLinkUrl(r.link_code)}
                          </a>
                          <button
                            type="button"
                            onClick={() => copyText(makeLinkUrl(r.link_code!))}
                            className="text-xs px-2 py-0.5 border rounded hover:bg-gray-50"
                            title={t('admin.coupons.actions.copy_link_url') || 'URLをコピー'}
                          >
                            {t('admin.coupons.actions.copy')}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </td>

                  <td className="px-4 py-2 hidden md:table-cell">{formatBenefit(r)}</td>
                  <td className="px-4 py-2 hidden lg:table-cell">{formatPeriod(r)}</td>

                  {/* 分析サマリー表示（+ 詳細） */}
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700">
                        {r.bound_users_total.toLocaleString()}
                      </span>
                      <button
                        type="button"
                        className={`text-xs px-2 py-1 border rounded transition-colors ${
                          r.bound_users_total > 0
                            ? 'hover:bg-gray-50 text-gray-700'
                            : 'opacity-40 cursor-not-allowed text-gray-400'
                        }`}
                        onClick={() => {
                          if (r.bound_users_total === 0) return;
                          setUsersCouponId(r.id); setUsersCouponName(r.name); setUsersCouponNote(r.note || null); setUsersOpen(true);
                        }}
                        title={r.bound_users_total > 0 ? (t('admin.common.view') || '詳細') : (t('admin.coupons.no_users') || 'バインドユーザーなし')}
                      >
                        {t('admin.common.view') || '詳細'}
                      </button>
                    </div>
                  </td>

                  <td className="px-4 py-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-yellow-50 text-yellow-700">
                      {r.clicks_total.toLocaleString()}
                    </span>
                  </td>

                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}
                    >
                      {r.is_active ? t('admin.coupons.status.active') : t('admin.coupons.status.inactive')}
                    </span>
                  </td>

                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/coupons/${r.id}`)}
                        className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                      >
                        {t('admin.common.view') || '詳細'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPosterCoupon(r); setPosterOpen(true); }}
                        className="text-xs px-2 py-1 border rounded hover:bg-rose-50 text-rose-700 border-rose-300"
                      >
                        ポスター
                      </button>
                      {/* 更新/削除は後端APIが揃ってから有効化 */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 簡易ページャ（total 未取得のため次へ押下で fetch。空になったら戻す運用） */}
          <div className="flex items-center justify-between p-3 border-t text-sm">
            <div>
              {t('admin.common.page')} {page}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('admin.common.prev')}
              </button>
              <button
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                disabled={sortedRows.length < pageSize}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('admin.common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 明細ダイアログ */}
      <AdminCouponUsersDialog
        open={usersOpen}
        couponId={usersCouponId}
        couponName={usersCouponName || undefined}
        couponNote={usersCouponNote || undefined}
        onClose={() => setUsersOpen(false)}
      />

      {/* ポスター生成ダイアログ */}
      <AdminCouponPosterDialog
        open={posterOpen}
        coupon={posterCoupon}
        linkUrl={posterCoupon?.link_code ? makeLinkUrl(posterCoupon.link_code) : undefined}
        onClose={() => { setPosterOpen(false); setPosterCoupon(null); }}
      />
    </div>
  );
};

export default AdminCouponList;
